import Cookies from "js-cookie";

/**
 * Finds a product variant based on selected options. First we try to find an exact match,
 * if that fails we try to find a partial match.
 *
 * @param {Array} variants - All available variants
 * @param {Array} currentOptions - Current option values
 * @param {number} optionIndex - Index of option being changed
 * @param {string} newValue - New value for the option
 * @returns {Object|undefined} Matching variant or undefined
 */
export function findVariantByOptionValue(
  variants,
  currentOptions,
  optionIndex,
  newValue
) {
  const targetOptions = [...currentOptions];
  targetOptions[optionIndex] = newValue;

  const exactMatch = variants.find((variant) =>
    variant.options.every((opt, index) => opt === targetOptions[index])
  );

  if (exactMatch) return exactMatch;

  return variants.find((variant) => variant.options[optionIndex] === newValue);
}

/**
 * Retrieves valid secondary options for a given option value and its index from a list of variants.
 * This function filters the variants to find those that match the given value at the specified option index.
 * It then maps over these variants to find and return options that are not at the given optionIndex,
 * effectively providing a list of secondary options that are compatible with the selected primary option value.
 *
 * @param {Array} variants - The array of variant objects, each containing an options array.
 * @param {number} optionPosition - The index of the option within the options array of a variant.
 * @param {string|number} value - The selected option value to filter the variants by.
 * @returns {Array} An array of options that are valid secondary options for the selected value.
 */
export function getValidSecondaryOptions(variants, optionPosition, value) {
  const validSecondaryVariants = variants.filter(
    (variant) => variant.options[optionPosition] === value
  );
  const validSecondaryOptions = validSecondaryVariants.map((variant) =>
    variant.options.find((v, i) => i !== optionPosition)
  );
  return validSecondaryOptions;
}

/**
 * Updates the current URL to include a variant ID query parameter without reloading the page.
 *
 * @param {number|string} variantId - The ID of the product variant to be added to the URL query parameters.
 */
export function updateUrlWithVariantId(variantId) {
  if (window.history && window.history.replaceState) {
    window.history.replaceState(
      {},
      window.title,
      `${window.location.pathname}?variant=${variantId}`
    );
  }
}

/**
 * Tracks cart addition events in Klaviyo.
 * This function sends a structured data object to Klaviyo to record when items are added to the shopping cart.
 *
 * @param {Object} cart - The cart object containing all the relevant information about the current cart state.
 * @param {number} cart.total_price - The total price of all items in the cart in cents.
 * @param {number} cart.total_discount - Total discount applied to the cart.
 * @param {number} cart.original_total_price - The original total price of the cart items without discounts.
 * @param {Array} cart.items - An array of items currently in the cart.
 */
export function updateKlaviyoAddToCartTracking(cart) {
  if (!_learnq) {
    console.warn("Klaviyo tracking object _learnq is not available.");
    return;
  }

  const fields = {
    total_price: cart.total_price / 100,
    $value: cart.total_price / 100,
    total_discount: cart.total_discount,
    original_total_price: cart.original_total_price / 100,
    items: cart.items,
  };

  _learnq.push(["track", "Added to Cart", fields]);
}

/**
 * Stores a recently viewed product ID in a cookie.
 * If the product ID is not already in the list of recent product IDs, it adds it to the front.
 * The list is maintained with a maximum of 10 recent IDs.
 *
 * @param {string|number} id - The product ID to store as recently viewed.
 */
export function storeRecentProductId(id) {
  const recentProductIdsRaw = Cookies.get("recentProductIds");
  let recentProductIds = [];

  try {
    recentProductIds = JSON.parse(recentProductIdsRaw) || [];
  } catch (error) {
    console.error("Error parsing recent product IDs:", error);
  }

  if (!recentProductIds.includes(id)) {
    recentProductIds = [id, ...recentProductIds].slice(0, 10);

    Cookies.set("recentProductIds", JSON.stringify(recentProductIds), {
      expires: 30,
      path: "/",
    });
  }
}

/**
 * Calculates the discounted price based on a selling plan's price adjustment
 * @param {Object} params - Function parameters
 * @param {number} params.basePrice - Original price before discount
 * @param {Object} params.priceAdjustment - Price adjustment configuration
 * @param {string} params.priceAdjustment.value_type - Type of adjustment ('percentage', 'fixed', etc)
 * @param {number} params.priceAdjustment.value - Adjustment value (percentage or fixed amount)
 * @returns {number|undefined} Discounted price if calculable, undefined otherwise
 */
export function calculateSellingPlanPrice({ basePrice, priceAdjustment }) {
  if (!priceAdjustment || !basePrice) return undefined;

  switch (priceAdjustment.value_type) {
    case "percentage":
      return basePrice * (1 - priceAdjustment.value / 100);
    case "fixed_amount":
      return basePrice - priceAdjustment.value;
    default:
      return undefined;
  }
}
